import {insertAfter} from "../utils/dom-utils.js";
import {render} from "preact";
import {AbBadge} from '../components/badge/badge.component.js';
import {appContext} from "../badge2.context.js";
import {I18nService} from "./i18n.service.js";
import {$inject} from "../utils/di-container.js";
import {LoggerService} from "./logger.service.js";
import {UrlService} from "./url.service.js";
import {Api2Service} from "./api2.service.js";
import {BadgeService} from "./badge.service.js";
import {normalizeUrl} from "../utils/url-utils.js";
import { FeatureService } from './feature.service.js';
import { GlobalService } from './global.service.js';
import { supportedTypes } from './badge-supported-types.js';
import { deepClone } from '../../test/util/deep-clone.mjs';
import { CssService } from "./css.service";
import { MetadataService } from "./metadata.service.js";

/**
 * Identifies a single badge of a specific type on the page.
 * Holds logic related to creating/rendering and disposing of/deleting the root badge component, according to its
 * placeholder.
 */
export class Badge {

  /** @type {UrlService} */
  _urlService;

  /** @type {Api2Service} */
  _apiService;

  /** @type {FeatureService} */
  _featureService

  /** @type {GlobalService} */
  _globalService;

  /** @type {CssService} */
  _cssService;

  /** @type {MetadataService} */
  _metadataService;

  /** @type {LoggerService} */
  _logger;

  /** @type {_BadgeType} */
  type;
  
  /** @type {_Company} */
  company;

  /** @type {_BrandPartner[]} */
  brands;

  /** @type {_Product} */
  product;

  /** @type {_Placeholder} */
  placeholder;

  /** @type {HTMLElement} */
  element;

  /** @type {boolean} */
  valid;

  /** @type {string} */
  validationInfo;

  /** @type {string} */
  validationError;

  /** @type {string} */
  renderPlace;

  /** @type {Promise<void>} */
  activating;

  /** @type {boolean} */
  debug = false;

  /**
   * @param {_BadgeType} type
   * @param {_Company} company owner of this badge
   * @param {_BrandPartner[]} brands relevant brands, if any
   * @param {_Product} [product] the associated product, if any
   */
  constructor(type, company, brands, product) {
    this.type = type;
    this.product = product;
    this.company = company;
    this.brands = brands;
  }

  [$inject](obtain) {
    this._urlService = obtain(UrlService);
    this._logger = obtain(LoggerService);
    this._apiService = obtain(Api2Service);
    this._badgeService = obtain(BadgeService);
    this._featureService = obtain(FeatureService);
    this._globalService = obtain(GlobalService);
    this._cssService = obtain(CssService);
    this._metadataService = obtain(MetadataService);
  }

  get flavor() {
    return this.placeholder.flavor;
  }

  /**
   * @returns {boolean}
   */
  isActive() {
    return !!this.element;
  }

  /**
   * @returns {boolean}
   */
  isActiveOrActivating() {
    return this.isActive();
  }

  /**
   * Applies checks to a badge before activation.
   * If validation fails, it will not activate.
   * @return {{ valid: boolean, [validationError]: string, [validationInfo]: string,[message]: string, [additionalData]: any }}
   */
  validate() {
    if (this.type.originalPlan) {
      // plan already changed due to upgrade or fallback, we should no longer change it
    } else {
      if (this.product && this.product.brandSponsored && this.type.plan !== 'premium') {
        // brand sponsored badge - upgrade to premium by default
        if (this.type.variant === 'island' || this.type.variant === 'mid' || this.type.plan !== 'free') {
          const originalBadgeStr = this.toString();
          this.type.originalPlan = this.type.plan;
          this.type.plan = 'premium';
          this._logger.info('badge/upgrade/bs', `Company badge is brand sponsored. Badge ${originalBadgeStr} will be upgraded to premium.`, () => ({ badge: this.toInfo() }));
        }
      } else if (this.product && this.product.officialBrandShop) {
        if (this.type.variant === 'island' || this.type.variant === 'mid') {
          if (this.type.plan !== 'premium') {
            // d2c badge - upgrade to premium by default
            const originalBadgeStr = this.toString();
            this.type.originalPlan = this.type.plan;
            this.type.plan = 'premium';
            this._logger.info('badge/upgrade/d2c', `Official brand shop detected. Badge ${originalBadgeStr} will be upgraded to premium.`, () => ({ badge: this.toInfo() }));
          }
        } else {
          // d2c unsupported badge
          const message = `Official brand shop detected. Badge ${this.toString()} is not supported.`;
          return { valid: false, validationError: 'unsupported-d2c', message }
        }
      }
    }

    // check if badge is supported
    if (!supportedTypes.some(([platform, plan, variant]) => (this.type.platform === platform) && (this.type.plan === plan) && (this.type.variant === variant))) {
      if (this.type.plan === 'free') {
        const message = `The ${this.type.variant} seal is not supported on FREE plans.`;
        return { valid: false, validationError: 'unsupported-free-plan', message };
      } else {
        const message = `Badge type not supported yet: ${this.toString()}`;
        return { valid: false, validationError: 'unsupported-type', message };
      }
    }

    if (this.type.options.enabled !== true) {
      const message = `Badge is not enabled`;
      return {valid: false, validationError: 'disabled', message};
    }

    if (!this.product && this.type.variant === 'island') {
      // cannot have islands without product, we need to invalidate this
      const message = `Badge variant "island" not allowed without product.`;
      return { valid: false, validationError: 'content-without-product', message };
    }

    // Homepage check (for free mid page badges)
    if (this.type.plan === 'free' && this.type.variant === 'mid') {
      const currentUrl = normalizeUrl(this._urlService.getCurrentUrl(), { noWww: true });
      const homepageUrl = normalizeUrl(this.company.homepage, { noWww: true });
      if (homepageUrl !== currentUrl && this._metadataService._badgeMeta?.environment !== 'app') {
        const message = `Current page "${currentUrl}" is semantically different than configured home-page "${homepageUrl}"`;
        return { valid: false, validationError: 'free-not-on-homepage', additionalData: { currentUrl, homepageUrl }, message };
      }
    }

    // Retailers without partners are not allowed badges
    if (this.brands.length === 0 && !this.type.t0) {
      const message = `Current retailer "${this.company.homepage}" has 0 partners. Badge will be disabled.`;
      return { valid: false, validationError: 'no-partners', message };
    }

    // Special rule: hide desktop sidebar when content badge already present
    if (this._badgeService.specialRules.hideSidebarWhenContentPresent && this.type.platform === 'desktop') {
      if (this.type.variant === 'mid') {
        // trying to activate sidebar => not allowed if content exists
        const contentBadge = this._badgeService.getBadge({ variant: 'island' });
        if (contentBadge && contentBadge.valid && contentBadge.isActiveOrActivating()) {
          const message = `Content badge already present and "hideSidebarWhenContentPresent" is enabled. Desktop sidebar will be skipped.`;
          return { valid: false, validationError: 'content-present-hide-sidebar', message };
        }
      } else if (this.type.variant === 'island') {
        // trying to activate content => sidebar will be disabled first if it exists
        const sidebarBadge = this._badgeService.getBadge({ variant: 'mid' });
        if (sidebarBadge && sidebarBadge.valid && sidebarBadge.isActiveOrActivating()) {
          const message = `Sidebar badge already present and "hideSidebarWhenContentPresent" is enabled. Desktop sidebar will be disabled in order to activate the content badge.`;
          return { valid: true, validationInfo: 'hide-existing-sidebar-to-show-content', message, preActivateFn: () => sidebarBadge.deactivate('hide-existing-sidebar-to-show-content') };
        }
      }
    }

    return { valid: true, validationInfo: 'ok' };
  }

  activate(reason) {
    if (!this.placeholder?.element) {
      this._logger.warn(`badge/invalid/no-element`, `Badge ${this.toString()} is not valid. Missing element in page.`, () => ({ badge: this.toInfo() }));
      return;
    }

    this._logger.debug(`badge/activating/${reason}`, `Activating badge: ${this.toString()}. Reason: ${reason}`, () => ({ badge: this.toInfo() }));

    const { valid, message, validationInfo, validationError, preActivateFn } = this.validate();

    this.valid = valid;
    this.validationInfo = validationInfo;
    this.validationError = validationError;

    if (valid) {
      if (message) {
        this._logger.debug(`badge/valid/${validationInfo}`, `Badge ${this.toString()} validation performed with message. ${message || ''}`, () => ({ badge: this.toInfo() }));
      }

      if (preActivateFn) {
        preActivateFn();
      }

      // make sure texts are available for this language
      appContext.obtain(I18nService).loadLanguage(this.type.language);

      // create a new root element for the rendered badge
      this.element = document.createElement(this.type.options.targetElementType);
      this.element.setAttribute('data-authorizedby-rendered-badge', this.type.variant);
      this.element.style.position = 'relative'; // will be used as reference point, e.g. for tooltips
      this.element.style.display = 'none'; // initially, all badges are hidden, because style is not yet loaded

      // using shadow dom - we need to import the style inside
      this.shadow = this.element.attachShadow({ mode: 'open' });
      this._cssService.importBadgeStyle(this);

      if (this.type.variant === 'header' && this.type.platform === 'mobile') {
        // special flow: add the rendered element directly in body (first element) for the mobile header aspect
        this._logger.info('badge/render-place/in-body', `Badge will be rendered directly in body: ${this.toString()}`, () => ({ badge: this.toInfo() }));
        this.renderPlace = 'body';
        document.body.prepend(this.element);
      } else {
        // normal flow: add the rendered element right next to the placeholder
        this._logger.debug('badge/render-place/next-to-placeholder', `Badge will be rendered after its placeholder: ${this.toString()}`, () => ({ badge: this.toInfo() }));
        this.renderPlace = 'after-placeholder';
        insertAfter(this.placeholder.element, this.element);
      }

      // actually render the badge
      this._render();
    } else {
      this._logger.warn(`badge/invalid/${validationError}`, `Badge ${this.toString()} is not valid. ${message || ''}`, () => ({ badge: this.toInfo() }));
    }
  }

  deactivate(reason) {
    this._logger.debug(`badge/de-activating/${reason}`, `De-activating badge: ${this.toString()}. Reason: ${reason}`, () => ({ badge: this.toInfo() }));
    if (this.element) {
      this.element.remove();
    }
    delete this.element;
  }

  /**
   * @private
   */
  _render() {
    const parent = this.shadow;
    const component = <AbBadge badge={this} parentElement={parent} />;
    render(component, parent);
  }

  /**
   * @param {_BadgePlatform} platform
   */
  changePlatform(platform) {
    if (!this.placeholder) {
      return;
    }
    if (this.type.platform !== platform) {
      this._logger.debug('badge/platform-switch', `Badge ${this.toString()} switching to platform: ${platform}`, () => ({ badge: this.toInfo(), platform }));
      this.type.platform = platform;
      this.refresh('platform-switch');
    }
  }

  /**
   * Re-checks the badge - and activates, de-activates or re-renders if/as necessary.
   * @property {string} reason
   * @returns {void}
   */
  refresh(reason) {
    this._logger.debug(`badge/refreshing/${reason}`, `Refreshing badge: ${this.toString()}. Reason: ${reason}`, () => ({ badge: this.toInfo() }));
    const { valid, message } = this.validate();
    if (valid) {
      if (this.isActive()) {
        this._render();
      } else {
        this.activate('refresh');
      }
    } else {
      if (this.isActive()) {
        this.deactivate('refresh');
      }
    }
  }

  /**
   * Sets debug mode for this badge.
   * @param debug
   */
  setDebug(debug) {
    this.debug = debug;
    this._render();
  }

  toString() {
    return `${this.type.platform}/${this.type.plan}/${this.type.variant}/${this.product ? (`product/${this.product.id || 'not-specific'}` + (this.product.brandSponsored ? ':BS' : '')) : 'shop'}`;
  }

  toInfo() {
    return {
      type: deepClone(this.type || {}),
      company: deepClone(this.company || {}),
      product: deepClone(this.product || {}),
      active: this.isActive(),
      valid: this.valid,
      validationInfo: this.validationInfo,
      validationError: this.validationError,
      renderPlace: this.renderPlace
    };
  }

}
